import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FileText, User, Briefcase, GraduationCap, Award, Plus, Trash2,
    Download, Sparkles, Eye, EyeOff, Save, Check, ChevronRight, ChevronLeft,
    Crown, Terminal, ArrowRight, MapPin, Mail, Phone, Globe, Linkedin,
    Calendar, Star, Code, FolderKanban, Link2, Rocket, Zap, Target,
    CheckCircle2, Circle, ArrowUpRight, Wand2, Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';
import { resumeAPI } from '../utils/api';
import authService from '../firebase/authService';

// Floating Particles Background
const ParticlesBackground = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-emerald-500/20 via-emerald-500/5 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-cyan-500/15 via-teal-500/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/4 w-[400px] h-[400px] bg-gradient-to-br from-violet-500/10 via-purple-500/5 to-transparent rounded-full blur-3xl" />
        <div
            className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: '40px 40px' }}
        />
    </div>
);

// Step indicator for wizard
const StepIndicator = ({ steps, currentStep, onStepClick }) => (
    <div className="flex items-center justify-center space-x-2 mb-12 flex-wrap gap-y-4">
        {steps.map((step, index) => (
            <React.Fragment key={step.id}>
                <motion.button
                    onClick={() => onStepClick(index)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className={`relative flex items-center justify-center w-11 h-11 rounded-2xl transition-all duration-500 ${index === currentStep
                        ? 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30'
                        : index < currentStep
                            ? 'bg-emerald-500/20 border-2 border-emerald-500/50'
                            : 'bg-white/5 border border-white/10 hover:border-white/30'
                        }`}
                >
                    {index < currentStep ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                        <step.icon className={`w-4 h-4 ${index === currentStep ? 'text-white' : 'text-slate-500'}`} />
                    )}
                </motion.button>
                {index < steps.length - 1 && (
                    <div className={`w-6 h-0.5 rounded-full transition-colors duration-500 hidden sm:block ${index < currentStep ? 'bg-emerald-500/50' : 'bg-white/10'}`} />
                )}
            </React.Fragment>
        ))}
    </div>
);

// Glowing Input
const GlowInput = ({ label, icon: Icon, value, onChange, placeholder, type = 'text', required }) => (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="group relative">
        <label className="flex items-center space-x-2 text-xs font-medium text-slate-400 mb-3 uppercase tracking-wider">
            {Icon && <Icon className="w-4 h-4 text-emerald-500" />}
            <span>{label}</span>
            {required && <span className="text-emerald-500">*</span>}
        </label>
        <div className="relative">
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-5 py-4 bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl text-white placeholder-slate-600 text-sm focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.05] transition-all duration-300"
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-500/0 via-emerald-500/5 to-teal-500/0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity pointer-events-none" />
        </div>
    </motion.div>
);

// Rich Textarea
const RichTextarea = ({ label, value, onChange, placeholder, rows = 4 }) => (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="group">
        <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</label>
            <div className="flex items-center space-x-2 text-[10px] text-slate-600">
                <span className="px-2 py-1 bg-white/5 rounded-lg">**bold**</span>
                <span className="px-2 py-1 bg-white/5 rounded-lg">*italic*</span>
            </div>
        </div>
        <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            className="w-full px-5 py-4 bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl text-white placeholder-slate-600 text-sm focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.05] transition-all duration-300 resize-none"
        />
    </motion.div>
);

// Entry Card
const EntryCard = ({ children, onRemove, index }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -20 }}
        className="relative p-6 bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-3xl group hover:border-white/20 transition-all duration-300"
    >
        <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-emerald-500/30">
            {index + 1}
        </div>
        <button onClick={onRemove} className="absolute top-4 right-4 p-2 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all opacity-0 group-hover:opacity-100">
            <Trash2 className="w-4 h-4" />
        </button>
        <div className="pt-2">{children}</div>
    </motion.div>
);

// Add Button
const AddButton = ({ onClick, label }) => (
    <motion.button
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className="w-full py-5 bg-gradient-to-r from-emerald-500/10 via-transparent to-teal-500/10 border-2 border-dashed border-emerald-500/30 rounded-3xl text-emerald-400 font-medium flex items-center justify-center space-x-3 hover:border-emerald-500/50 transition-all duration-300"
    >
        <div className="w-8 h-8 bg-emerald-500/20 rounded-xl flex items-center justify-center">
            <Plus className="w-5 h-5" />
        </div>
        <span>{label}</span>
    </motion.button>
);

// Skills Tag Cloud
const SkillsCloud = ({ skills, onChange }) => {
    const [input, setInput] = useState('');
    const addSkill = () => {
        if (input.trim() && !skills.includes(input.trim())) {
            onChange([...skills, input.trim()]);
            setInput('');
        }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex flex-wrap gap-3 min-h-[60px] p-4 bg-white/[0.02] rounded-2xl border border-white/5">
                <AnimatePresence>
                    {skills.map((skill) => (
                        <motion.span
                            key={skill}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            whileHover={{ scale: 1.05 }}
                            onClick={() => onChange(skills.filter(s => s !== skill))}
                            className="cursor-pointer px-4 py-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-emerald-300 rounded-2xl flex items-center space-x-2 hover:from-red-500/20 hover:to-red-500/20 hover:border-red-500/30 hover:text-red-400 transition-all"
                        >
                            <span className="font-medium">{skill}</span>
                        </motion.span>
                    ))}
                </AnimatePresence>
                {skills.length === 0 && <span className="text-slate-600 text-sm">Add your skills below...</span>}
            </div>
            <div className="flex space-x-3">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ',') && (e.preventDefault(), addSkill())}
                    placeholder="Type a skill and press Enter..."
                    className="flex-1 px-5 py-4 bg-white/[0.03] border border-white/10 rounded-2xl text-white placeholder-slate-600 text-sm focus:outline-none focus:border-emerald-500/50 transition-all"
                />
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={addSkill} className="px-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl font-medium shadow-lg shadow-emerald-500/20">
                    <Plus className="w-5 h-5 text-white" />
                </motion.button>
            </div>
        </motion.div>
    );
};

const ResumeBuilderPage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isTailoring, setIsTailoring] = useState(false);
    const [tailoringComplete, setTailoringComplete] = useState(false);
    const [usage, setUsage] = useState({ used: 0, limit: 2, remaining: 2, unlimited: false });

    const [resumeData, setResumeData] = useState({
        full_name: '', email: '', phone: '', location: '', linkedin: '', portfolio: '', summary: '',
        experience: [], education: [], skills: [], projects: [], certifications: [],
        target_role: '', job_description: ''
    });

    useEffect(() => {
        const unsubscribe = authService.onAuthStateChange((currentUser) => {
            setUser(currentUser);
            if (currentUser) loadUsage();
        });
        try {
            const saved = localStorage.getItem('resume_draft');
            if (saved) setResumeData(JSON.parse(saved));
        } catch (e) { }
        return () => unsubscribe();
    }, []);

    const loadUsage = async () => {
        try { setUsage(await resumeAPI.getUsage()); } catch (e) { }
    };

    const saveDraft = () => {
        localStorage.setItem('resume_draft', JSON.stringify(resumeData));
        toast.success('Progress saved!');
    };

    const handleInputChange = (field, value) => setResumeData(prev => ({ ...prev, [field]: value }));
    const addEntry = (field, template) => setResumeData(prev => ({ ...prev, [field]: [...prev[field], template] }));
    const updateEntry = (field, index, key, value) => {
        const updated = [...resumeData[field]];
        updated[index] = { ...updated[index], [key]: value };
        setResumeData(prev => ({ ...prev, [field]: updated }));
    };
    const removeEntry = (field, index) => setResumeData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));

    const handleGenerate = async () => {
        if (!resumeData.full_name || !resumeData.email) {
            toast.error('Name and email are required');
            return;
        }
        if (!user) {
            toast.error('Please sign in to download');
            return;
        }
        setIsGenerating(true);
        try {
            await resumeAPI.generatePdf(resumeData);
            toast.success('Resume downloaded!');
            await loadUsage();
        } catch (error) {
            toast.error(error.isLimitError ? 'Limit reached. Upgrade for more!' : 'Download failed');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleTailorResume = async () => {
        if (!resumeData.target_role && !resumeData.job_description) {
            toast.error('Please enter a target role or job description');
            return;
        }
        if (!user) {
            toast.error('Please sign in to use AI tailoring');
            return;
        }

        setIsTailoring(true);
        try {
            const response = await resumeAPI.tailorResume(resumeData);
            if (response.tailored_resume) {
                setResumeData(prev => ({
                    ...prev,
                    summary: response.tailored_resume.summary || prev.summary,
                    experience: response.tailored_resume.experience || prev.experience,
                    skills: response.tailored_resume.skills || prev.skills
                }));
                setTailoringComplete(true);
                toast.success('Resume tailored successfully!');
            }
        } catch (error) {
            console.error('Tailoring error:', error);
            toast.error('AI tailoring failed. You can still download your resume.');
        } finally {
            setIsTailoring(false);
        }
    };

    const steps = [
        { id: 'personal', label: 'Personal', icon: User },
        { id: 'experience', label: 'Experience', icon: Briefcase },
        { id: 'education', label: 'Education', icon: GraduationCap },
        { id: 'skills', label: 'Skills', icon: Sparkles },
        { id: 'projects', label: 'Projects', icon: FolderKanban },
        { id: 'certifications', label: 'Certs', icon: Award },
        { id: 'tailor', label: 'Tailor', icon: Wand2 },
        { id: 'finish', label: 'Finish', icon: Rocket }
    ];

    const renderStep = () => {
        switch (currentStep) {
            case 0: // Personal
                return (
                    <motion.div key="personal" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <GlowInput label="Full Name" icon={User} value={resumeData.full_name} onChange={(v) => handleInputChange('full_name', v)} placeholder="John Doe" required />
                            <GlowInput label="Email" icon={Mail} value={resumeData.email} onChange={(v) => handleInputChange('email', v)} placeholder="john@example.com" type="email" required />
                            <GlowInput label="Phone" icon={Phone} value={resumeData.phone} onChange={(v) => handleInputChange('phone', v)} placeholder="+1 555 123 4567" />
                            <GlowInput label="Location" icon={MapPin} value={resumeData.location} onChange={(v) => handleInputChange('location', v)} placeholder="San Francisco, CA" />
                            <GlowInput label="LinkedIn" icon={Linkedin} value={resumeData.linkedin} onChange={(v) => handleInputChange('linkedin', v)} placeholder="linkedin.com/in/johndoe" />
                            <GlowInput label="Portfolio" icon={Globe} value={resumeData.portfolio} onChange={(v) => handleInputChange('portfolio', v)} placeholder="johndoe.dev" />
                        </div>
                        <RichTextarea label="Professional Summary" value={resumeData.summary} onChange={(v) => handleInputChange('summary', v)} placeholder="Results-driven software engineer with 5+ years building scalable systems..." rows={4} />
                    </motion.div>
                );

            case 1: // Experience
                return (
                    <motion.div key="experience" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                        <AnimatePresence>
                            {resumeData.experience.map((exp, i) => (
                                <EntryCard key={i} index={i} onRemove={() => removeEntry('experience', i)}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <GlowInput label="Company" icon={Briefcase} value={exp.company} onChange={(v) => updateEntry('experience', i, 'company', v)} placeholder="Google" />
                                        <GlowInput label="Position" icon={Star} value={exp.position} onChange={(v) => updateEntry('experience', i, 'position', v)} placeholder="Senior Engineer" />
                                        <GlowInput label="Start Date" icon={Calendar} value={exp.start_date} onChange={(v) => updateEntry('experience', i, 'start_date', v)} placeholder="Jan 2020" />
                                        <GlowInput label="End Date" icon={Calendar} value={exp.end_date} onChange={(v) => updateEntry('experience', i, 'end_date', v)} placeholder="Present" />
                                    </div>
                                    <RichTextarea label="Achievements" value={(exp.highlights || []).join('\n')} onChange={(v) => updateEntry('experience', i, 'highlights', v.split('\n').filter(h => h.trim()))} placeholder="• Led team of 8 engineers" rows={4} />
                                </EntryCard>
                            ))}
                        </AnimatePresence>
                        <AddButton onClick={() => addEntry('experience', { company: '', position: '', start_date: '', end_date: 'Present', highlights: [] })} label="Add Work Experience" />
                    </motion.div>
                );

            case 2: // Education
                return (
                    <motion.div key="education" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                        <AnimatePresence>
                            {resumeData.education.map((edu, i) => (
                                <EntryCard key={i} index={i} onRemove={() => removeEntry('education', i)}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <GlowInput label="Institution" icon={GraduationCap} value={edu.institution} onChange={(v) => updateEntry('education', i, 'institution', v)} placeholder="Stanford University" />
                                        <GlowInput label="Degree" icon={Award} value={edu.degree} onChange={(v) => updateEntry('education', i, 'degree', v)} placeholder="Bachelor of Science" />
                                        <GlowInput label="Field of Study" value={edu.field} onChange={(v) => updateEntry('education', i, 'field', v)} placeholder="Computer Science" />
                                        <GlowInput label="Graduation Date" icon={Calendar} value={edu.graduation_date} onChange={(v) => updateEntry('education', i, 'graduation_date', v)} placeholder="May 2020" />
                                    </div>
                                </EntryCard>
                            ))}
                        </AnimatePresence>
                        <AddButton onClick={() => addEntry('education', { institution: '', degree: '', field: '', graduation_date: '' })} label="Add Education" />
                    </motion.div>
                );

            case 3: // Skills
                return (
                    <motion.div key="skills" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                        <p className="text-slate-400 mb-6">Add your technical skills, tools, and technologies. Click a skill to remove it.</p>
                        <SkillsCloud skills={resumeData.skills} onChange={(s) => handleInputChange('skills', s)} />
                    </motion.div>
                );

            case 4: // Projects
                return (
                    <motion.div key="projects" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                        <AnimatePresence>
                            {resumeData.projects.map((proj, i) => (
                                <EntryCard key={i} index={i} onRemove={() => removeEntry('projects', i)}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <GlowInput label="Project Name" icon={FolderKanban} value={proj.name} onChange={(v) => updateEntry('projects', i, 'name', v)} placeholder="E-commerce Platform" />
                                        <GlowInput label="Technologies" icon={Code} value={proj.technologies} onChange={(v) => updateEntry('projects', i, 'technologies', v)} placeholder="React, Node.js" />
                                    </div>
                                    <RichTextarea label="Description" value={proj.description || ''} onChange={(v) => updateEntry('projects', i, 'description', v)} placeholder="• Built scalable API handling 10K req/sec" rows={3} />
                                </EntryCard>
                            ))}
                        </AnimatePresence>
                        <AddButton onClick={() => addEntry('projects', { name: '', technologies: '', description: '' })} label="Add Project" />
                    </motion.div>
                );

            case 5: // Certifications
                return (
                    <motion.div key="certifications" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                        <AnimatePresence>
                            {resumeData.certifications.map((cert, i) => (
                                <EntryCard key={i} index={i} onRemove={() => removeEntry('certifications', i)}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <GlowInput label="Certification" icon={Award} value={cert.name} onChange={(v) => updateEntry('certifications', i, 'name', v)} placeholder="AWS Solutions Architect" />
                                        <GlowInput label="Issuer" icon={Briefcase} value={cert.issuer} onChange={(v) => updateEntry('certifications', i, 'issuer', v)} placeholder="Amazon" />
                                        <GlowInput label="Date" icon={Calendar} value={cert.date} onChange={(v) => updateEntry('certifications', i, 'date', v)} placeholder="March 2023" />
                                        <GlowInput label="Credential ID" value={cert.credential_id} onChange={(v) => updateEntry('certifications', i, 'credential_id', v)} placeholder="ABC123" />
                                    </div>
                                </EntryCard>
                            ))}
                        </AnimatePresence>
                        <AddButton onClick={() => addEntry('certifications', { name: '', issuer: '', date: '', credential_id: '' })} label="Add Certification" />
                    </motion.div>
                );

            case 6: // Tailor
                return (
                    <motion.div key="tailor" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                        <div className="text-center mb-8">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-violet-500/30"
                            >
                                <Wand2 className="w-10 h-10 text-white" />
                            </motion.div>
                            <h2 className="text-2xl font-bold text-white mb-3">AI-Powered Tailoring</h2>
                            <p className="text-slate-400 max-w-lg mx-auto">
                                Tell us the role you're targeting or paste a job description. Our AI will optimize your resume for maximum ATS score.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <GlowInput
                                label="Target Role"
                                icon={Target}
                                value={resumeData.target_role}
                                onChange={(v) => handleInputChange('target_role', v)}
                                placeholder="e.g., Senior Software Engineer, Product Manager"
                            />

                            <div className="relative py-4">
                                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 px-4 py-1 bg-[#030305] text-slate-500 text-xs font-medium z-10">
                                    OR
                                </div>
                                <div className="border-t border-white/10" />
                            </div>

                            <RichTextarea
                                label="Job Description (paste the full JD for best results)"
                                value={resumeData.job_description}
                                onChange={(v) => handleInputChange('job_description', v)}
                                placeholder="Paste the job description here. The AI will analyze key requirements and keywords to optimize your resume."
                                rows={8}
                            />
                        </div>

                        {tailoringComplete ? (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-6 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-3xl"
                            >
                                <div className="flex items-center space-x-3 mb-3">
                                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                                    <h3 className="text-lg font-bold text-white">Resume Tailored!</h3>
                                </div>
                                <p className="text-slate-400 text-sm">
                                    Your summary, experience highlights, and skills have been optimized. Click Continue to download.
                                </p>
                            </motion.div>
                        ) : (
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleTailorResume}
                                disabled={isTailoring || (!resumeData.target_role && !resumeData.job_description)}
                                className="w-full py-5 bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl text-white font-bold flex items-center justify-center space-x-3 shadow-lg shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isTailoring ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>AI is optimizing your resume...</span>
                                    </>
                                ) : (
                                    <>
                                        <Wand2 className="w-5 h-5" />
                                        <span>Tailor My Resume</span>
                                        <Sparkles className="w-4 h-4" />
                                    </>
                                )}
                            </motion.button>
                        )}

                        <p className="text-center text-slate-600 text-xs">
                            Skip this step if you want to download without tailoring
                        </p>
                    </motion.div>
                );

            case 7: // Finish
                return (
                    <motion.div key="finish" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", delay: 0.2 }}
                            className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-emerald-500/30"
                        >
                            <Rocket className="w-12 h-12 text-white" />
                        </motion.div>
                        <h2 className="text-3xl font-bold text-white mb-4">Your Resume is Ready!</h2>
                        <p className="text-slate-400 max-w-md mx-auto mb-8">
                            Download your ATS-optimized PDF resume and start landing interviews.
                        </p>

                        {!user ? (
                            /* Prominent Sign-in Card for non-authenticated users */
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="max-w-md mx-auto p-8 bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/10 border border-emerald-500/30 rounded-3xl mb-8"
                            >
                                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30">
                                    <User className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">Sign in to Download</h3>
                                <p className="text-slate-400 text-sm mb-6">
                                    Create a free account to download your resume. It takes 30 seconds!
                                </p>
                                <ul className="text-left space-y-3 mb-6">
                                    <li className="flex items-center space-x-3 text-sm">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                        <span className="text-slate-300">2 free resume downloads</span>
                                    </li>
                                    <li className="flex items-center space-x-3 text-sm">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                        <span className="text-slate-300">Save & edit anytime</span>
                                    </li>
                                    <li className="flex items-center space-x-3 text-sm">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                        <span className="text-slate-300">Access AI career roadmaps</span>
                                    </li>
                                </ul>
                                <Link
                                    to="/build-roadmap"
                                    className="w-full px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl text-white font-bold flex items-center justify-center space-x-2 shadow-lg shadow-emerald-500/30 hover:scale-105 transition-transform"
                                >
                                    <span>Sign In Free</span>
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                                <p className="text-slate-600 text-xs mt-4">No credit card required</p>
                            </motion.div>
                        ) : (
                            <>
                                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/5 rounded-2xl mb-8">
                                    <FileText className="w-4 h-4 text-emerald-400" />
                                    <span className="text-sm text-slate-300">
                                        {usage.unlimited ? 'Unlimited downloads' : `${usage.remaining} downloads remaining`}
                                    </span>
                                </div>

                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    {!usage.unlimited && usage.remaining <= 0 ? (
                                        <Link to="/subscribe" className="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl text-white font-bold shadow-lg shadow-amber-500/30 hover:scale-105 transition-transform flex items-center space-x-2">
                                            <Crown className="w-5 h-5" />
                                            <span>Upgrade for More</span>
                                        </Link>
                                    ) : (
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={handleGenerate}
                                            disabled={isGenerating}
                                            className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl text-white font-bold shadow-lg shadow-emerald-500/30 flex items-center space-x-3 disabled:opacity-50"
                                        >
                                            {isGenerating ? (
                                                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                                            ) : (
                                                <Download className="w-5 h-5" />
                                            )}
                                            <span>{isGenerating ? 'Generating...' : 'Download PDF'}</span>
                                            {!isGenerating && <ArrowRight className="w-4 h-4" />}
                                        </motion.button>
                                    )}
                                </div>
                            </>
                        )}
                    </motion.div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-[#030305] text-white overflow-hidden">
            <ParticlesBackground />

            <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-full mb-6"
                    >
                        <Zap className="w-4 h-4 text-emerald-400" />
                        <span className="text-emerald-400 text-sm font-medium">Free ATS-Optimized Builder</span>
                    </motion.div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white via-white to-slate-400 bg-clip-text text-transparent">
                        Build Your Resume
                    </h1>
                </motion.div>

                {/* Save button */}
                <div className="flex justify-end mb-6">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={saveDraft}
                        className="flex items-center space-x-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all"
                    >
                        <Save className="w-4 h-4" />
                        <span className="text-sm">Save Progress</span>
                    </motion.button>
                </div>

                {/* Step Indicator */}
                <StepIndicator steps={steps} currentStep={currentStep} onStepClick={setCurrentStep} />

                {/* Step Title */}
                <motion.div key={currentStep} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8">
                    <h2 className="text-2xl font-bold text-white mb-2">{steps[currentStep].label}</h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full" />
                </motion.div>

                {/* Step Content */}
                <AnimatePresence mode="wait">
                    {renderStep()}
                </AnimatePresence>

                {/* Navigation */}
                <div className="flex justify-between mt-12">
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                        disabled={currentStep === 0}
                        className="flex items-center space-x-2 px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-slate-400 hover:text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="w-5 h-5" />
                        <span>Previous</span>
                    </motion.button>

                    {currentStep < steps.length - 1 && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
                            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl text-white font-medium shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 transition-all"
                        >
                            <span>Continue</span>
                            <ChevronRight className="w-5 h-5" />
                        </motion.button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResumeBuilderPage;
